import { useEffect, useRef, useState } from 'react';

// Hardcoded default assignees — matches the backend's ALLOWED_ASSIGNEES.
// The backend accepts any string ≤100 chars, so users can supply a
// custom name via the "Other..." option.
export const ASSIGNEES = ['Tiffani D.', 'Abbey V.', 'Tara Z.', 'Emily B.'];

// Sentinel values used only by the <select> control.
const OPEN = '';
const OTHER = '__other__';
const EDIT = '__edit__';
const CLEAR = '__clear__';

const MAX_NAME_LEN = 100;

export default function AssigneeDropdown({ value, onChange, disabled = false }) {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const isStandard = value == null || ASSIGNEES.includes(value);
  const isCustom = !!value && !ASSIGNEES.includes(value);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  async function commit(next) {
    if (next === value) return;
    try {
      setBusy(true);
      await onChange(next);
    } finally {
      setBusy(false);
    }
  }

  function enterEditMode(seed) {
    setInputValue(seed ?? '');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setInputValue('');
  }

  async function confirmEdit() {
    const trimmed = inputValue.trim();
    if (trimmed.length > MAX_NAME_LEN) return;  // safety; input already max=100
    const next = trimmed === '' ? null : trimmed;
    setEditing(false);
    await commit(next);
  }

  async function handleSelectChange(e) {
    const choice = e.target.value;
    if (choice === OTHER) {
      enterEditMode('');
      return;
    }
    if (choice === EDIT) {
      enterEditMode(value || '');
      return;
    }
    if (choice === CLEAR) {
      await commit(null);
      return;
    }
    if (choice === OPEN) {
      await commit(null);
      return;
    }
    await commit(choice);  // standard name
  }

  if (editing) {
    return (
      <span className="assignee-input-wrap">
        <input
          ref={inputRef}
          type="text"
          className="assignee-input"
          value={inputValue}
          maxLength={MAX_NAME_LEN}
          placeholder="Type a name..."
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              confirmEdit();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancelEdit();
            }
          }}
          disabled={disabled || busy}
          aria-label="Custom assignee name"
        />
        <button
          type="button"
          className="assignee-input__btn assignee-input__btn--ok"
          onClick={confirmEdit}
          disabled={disabled || busy}
          aria-label="Confirm assignee"
          title="Confirm (Enter)"
        >
          ✓
        </button>
        <button
          type="button"
          className="assignee-input__btn assignee-input__btn--cancel"
          onClick={cancelEdit}
          disabled={disabled || busy}
          aria-label="Cancel"
          title="Cancel (Esc)"
        >
          ×
        </button>
      </span>
    );
  }

  return (
    <select
      className="assignee-dropdown"
      value={value || OPEN}
      onChange={handleSelectChange}
      disabled={disabled || busy}
      aria-label="Assigned to"
    >
      <option value={OPEN}>Open</option>
      {ASSIGNEES.map((name) => (
        <option key={name} value={name}>
          {name}
        </option>
      ))}
      {isCustom && (
        // Synthesized option so <select> can render the current custom name.
        <option value={value}>{value}</option>
      )}
      <option disabled>──────────</option>
      {isCustom && <option value={EDIT}>Edit custom name…</option>}
      {isCustom && <option value={CLEAR}>Clear (set to Open)</option>}
      <option value={OTHER}>Other…</option>
    </select>
  );
}
